import { useState, useRef, useEffect } from 'react';
import { X, Upload, Trash2 } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import { dialog } from '../../../store/dialogStore';
import { WORKSPACE_ICONS, WORKSPACE_ICON_NAMES, WORKSPACE_ICON_COLORS, defaultColorForWorkspace } from './workspaceIcons';

function Preview({ name, icon, iconColor, iconUrl, size = 56 }) {
  const color = iconColor || defaultColorForWorkspace(0);
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className="rounded-lg object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const IconComp = icon ? WORKSPACE_ICONS[icon] : null;
  if (IconComp) {
    return (
      <div
        className="rounded-lg flex items-center justify-center"
        style={{ width: size, height: size, backgroundColor: color }}
      >
        <IconComp className="text-white" style={{ width: size * 0.55, height: size * 0.55 }} />
      </div>
    );
  }
  const letter = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      className="rounded-lg flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.45 }}
    >
      {letter}
    </div>
  );
}

export default function WorkspaceEditModal({ workspace, onClose }) {
  const { updateWorkspace, uploadWorkspaceIcon, deleteWorkspaceIcon } = useBoardStore();
  const [name, setName] = useState(workspace.name);
  const [icon, setIcon] = useState(workspace.icon || null);
  const [iconColor, setIconColor] = useState(workspace.icon_color || defaultColorForWorkspace(workspace.id));
  const [iconUrl, setIconUrl] = useState(workspace.icon_url || null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pickIcon = (name_) => {
    setIcon(name_);
    setIconUrl(null);
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const ws = await uploadWorkspaceIcon(workspace.id, file);
      setIconUrl(ws.icon_url);
      setIcon(null);
    } catch (err) {
      dialog.alert('Erro no upload', err.message);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeCustomIcon = async () => {
    try {
      await deleteWorkspaceIcon(workspace.id);
      setIconUrl(null);
    } catch (err) {
      dialog.alert('Erro', err.message);
    }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return dialog.alert('Nome obrigatório', 'Informe um nome para a área de trabalho.');
    setSaving(true);
    try {
      await updateWorkspace(workspace.id, {
        name: trimmed,
        icon: iconUrl ? null : icon,
        icon_color: iconColor,
      });
      onClose();
    } catch (err) {
      dialog.alert('Erro ao salvar', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[520px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h3 className="text-sm font-semibold text-text-primary">Editar área de trabalho</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          <div className="flex items-center gap-4">
            <Preview name={name} icon={icon} iconColor={iconColor} iconUrl={iconUrl} size={64} />
            <div className="flex-1">
              <label className="block text-xs text-text-secondary mb-1">Nome</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-main border border-border rounded px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
                autoFocus
              />
            </div>
          </div>

          {!iconUrl && (
            <>
              <div>
                <div className="text-xs text-text-secondary mb-2">Ícone</div>
                <div className="grid grid-cols-8 gap-1.5">
                  <button
                    onClick={() => setIcon(null)}
                    className={`aspect-square rounded border flex items-center justify-center text-xs transition-colors ${
                      !icon ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/40'
                    }`}
                    title="Letra (padrão)"
                  >
                    <span className="text-text-primary font-semibold">A</span>
                  </button>
                  {WORKSPACE_ICON_NAMES.map((n) => {
                    const IconComp = WORKSPACE_ICONS[n];
                    return (
                      <button
                        key={n}
                        onClick={() => pickIcon(n)}
                        className={`aspect-square rounded border flex items-center justify-center transition-colors ${
                          icon === n ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/40'
                        }`}
                        title={n}
                      >
                        <IconComp className="w-4 h-4 text-text-primary" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-xs text-text-secondary mb-2">Cor</div>
                <div className="flex gap-2 flex-wrap">
                  {WORKSPACE_ICON_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setIconColor(c)}
                      className={`w-7 h-7 rounded transition-transform ${
                        iconColor === c ? 'ring-2 ring-offset-2 ring-offset-card ring-white scale-110' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="pt-3 border-t border-border/50">
            <div className="text-xs text-text-secondary mb-2">Ícone personalizado</div>
            {iconUrl ? (
              <div className="flex items-center gap-3">
                <img src={iconUrl} alt="" className="w-12 h-12 rounded border border-border object-cover" />
                <button
                  onClick={removeCustomIcon}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-danger hover:bg-danger/10 rounded transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remover
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-primary border border-border hover:border-accent/40 rounded transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Enviar imagem
                </button>
                <div className="text-[11px] text-text-secondary/70 mt-1.5">PNG, JPG ou SVG até 2 MB</div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border/50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 bg-accent text-white text-sm rounded hover:bg-accent/80 transition-colors disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
